import * as React from "react";
import keycode from "keycode";
import cx from "classnames";
import { Flipped } from "react-flip-toolkit";
import { isBrowser } from "@vingle/wtf-sdk/helpers/envChecker";
import {
  useEventCallback,
  useEventListener,
} from "@vingle/wtf-sdk/helpers/hook";
import TrendingSectionBackground from "../trendingSectionBackground";
import shouldFlipCallback from "app/discover/helpers/shouldFlipCallback";
import hexToRgba from "@vingle/wtf-sdk/helpers/hexToRgba";

const styles = require("./trendingLanding.scss");

interface IProps {
  sections: Vingle.Discover.ITrendingSection[];
  initialActiveSection?: Vingle.Discover.ITrendingSection;
  onChangeActiveSection?: (section: Vingle.Discover.ITrendingSection) => void;
}

const TrendingSectionItem = React.forwardRef(
  (
    props: {
      section: Vingle.Discover.ITrendingSection;
      activeSection: Vingle.Discover.ITrendingSection;
      onClick: (section: Vingle.Discover.ITrendingSection) => void;
    },
    forwardRef: React.MutableRefObject<HTMLLIElement | null>,
  ) => {
    const { section, activeSection, onClick } = props;
    const handleClick = useEventCallback(() => {
      onClick(section);
    }, [section, onClick]);
    return (
      <li
        className={cx(styles.item, {
          [styles.active]: activeSection.id === section.id,
        })}
        ref={forwardRef}
      >
        <dl className={styles.section} onClick={handleClick}>
          <dt className={styles.sectionTitle} title={section.title}>
            <span
              className={styles.sectionTitleValue}
              style={{
                color:
                  section.id === activeSection.id
                    ? hexToRgba(section.display.titleColor)
                    : hexToRgba(activeSection.display.subtitleColor),
              }}
            >
              {section.title}
            </span>
          </dt>
          <dd
            className={styles.sectionDesc}
            style={{ color: hexToRgba(section.display.subtitleColor) }}
          >
            {section.desc}
          </dd>
        </dl>
      </li>
    );
  },
);

const makeWrapperStyleTemplate = (x: number, initial: boolean = false) => ({
  transform: `translate3d(${x}px, -50%, 0)`,
  transitionDuration: initial ? "0ms" : undefined,
});

function useTrendingLandingHook(props: IProps) {
  const { sections, initialActiveSection, onChangeActiveSection } = props;
  const [activeSection, setActiveSection] = React.useState(
    initialActiveSection || sections[0],
  );
  React.useEffect(() => {
    if (initialActiveSection && initialActiveSection.id !== activeSection.id) {
      setActiveSection(initialActiveSection);
    }
  }, [initialActiveSection]);
  const activeRef = React.useRef<HTMLLIElement>(null);
  const [initialSetActive, changeInitialSetActive] = React.useState(true);
  const [wrapperStyle, setWrapperStyle] = React.useState(() =>
    makeWrapperStyleTemplate(0),
  );
  const handleChangeActiveSection = React.useCallback(
    (section: Vingle.Discover.ITrendingSection) => {
      setActiveSection(section);
      if (onChangeActiveSection) {
        onChangeActiveSection(section);
      }
    },
    [onChangeActiveSection],
  );
  if (isBrowser()) {
    React.useLayoutEffect(() => {
      if (activeRef.current) {
        setWrapperStyle(
          makeWrapperStyleTemplate(
            -activeRef.current.offsetLeft - activeRef.current.offsetWidth / 2,
            initialSetActive,
          ),
        );
        changeInitialSetActive(false);
      }
    }, [activeSection]);

    useEventListener("keyup", e => {
      if (e.keyCode === keycode("left") || e.keyCode === keycode("right")) {
        let targetSection: Vingle.Discover.ITrendingSection | null = null;
        const currentSectionIndex = sections.findIndex(
          section => activeSection.id === section.id,
        );

        if (currentSectionIndex > -1) {
          if (e.keyCode === keycode("left")) {
            targetSection = sections[currentSectionIndex - 1];
          }

          if (e.keyCode === keycode("right")) {
            targetSection = sections[currentSectionIndex + 1];
          }
        }

        if (targetSection) {
          handleChangeActiveSection(targetSection);
        }
      }
    });
  }

  return {
    activeSection,
    wrapperStyle,
    activeRef,
    handleChangeActiveSection,
  };
}

export default React.memo(function TrendingLanding(props: IProps) {
  const { sections } = props;
  if (!sections.length) {
    return null;
  }
  const {
    wrapperStyle,
    activeRef,
    activeSection,
    handleChangeActiveSection,
  } = useTrendingLandingHook(props);
  return (
    <div className={styles.container}>
      <div className={styles.slide}>
        <div className={styles.wrapper} style={wrapperStyle}>
          <ul className={styles.list}>
            {sections.map(section => (
              <TrendingSectionItem
                key={section.id}
                ref={activeSection.id === section.id ? activeRef : undefined}
                section={section}
                activeSection={activeSection}
                onClick={handleChangeActiveSection}
              />
            ))}
          </ul>
        </div>
      </div>
      <div className={styles.backgroundWrapper}>
        {sections.map(section => {
          const isActive = activeSection.id === section.id;
          const background = (
            <TrendingSectionBackground
              section={section}
              className={cx(styles.background, { [styles.active]: isActive })}
              key={section.id}
            />
          );
          return isActive ? (
            <Flipped
              key={section.id}
              flipId={`discoverSectionImage__${section.id}`}
              shouldFlip={shouldFlipCallback}
              transformOrigin="0% 100%"
              opacity={false}
            >
              {background}
            </Flipped>
          ) : (
            background
          );
        })}
      </div>
    </div>
  );
});
